'use client'

/**
 * Dub's click-tracking script — the client half of affiliate attribution.
 *
 * When an affiliate's short link (`<NEXT_PUBLIC_DUB_REFER_DOMAIN>/<key>`)
 * redirects to `/demo/<slug>?dub_id=…`, this script stores the click id as the
 * first-party `dub_id` cookie (90 days). `POST /api/demo/sessions` then reads
 * that cookie server-side and stamps it on the demo session, which is the
 * only place the click ever needs to be: the lead is tracked at signup from
 * the SESSION's stored id, on whatever device the prospect signs up from.
 *
 * Two things this component refuses to do, on purpose:
 *
 *   - Load anywhere GA does not. Capability URLs (`/demo/s/<token>`, `/q/…`)
 *     and customer surfaces are excluded by the SAME predicate GA uses
 *     (`lib/analytics/first-party-tracking-scope.ts`). In particular the
 *     prospect's shareable demo link never runs this script — it would write
 *     `dub_partner_data` (the affiliate's name and photo) into the prospect's
 *     browser, and the affiliate is invisible to the prospect by construction.
 *   - Load at all until `NEXT_PUBLIC_DUB_REFER_DOMAIN` is set. Unset means the
 *     Dub program is not configured; nothing is fetched from Dub's CDN.
 *   - Load on an enrollment URL that carries a live bearer credential —
 *     `/signup?token=…`, `/enroll?token=…`, and the enrollment-RESUME
 *     `/login?token=…&email=…` that `app/enroll/page.tsx` sends a signed-out
 *     invitee to. `usePathname()` cannot see the query, so the predicate is
 *     handed `window.location.search` too. The
 *     credential path that matters arrives by HARD navigation
 *     (`components/demo/BuildTheater.tsx` uses `window.location.assign`), so
 *     the document is fresh and the script is never injected. A SOFT
 *     navigation from an already-tracked page is a residual: the injected
 *     script stays in `document.head` and `@dub/analytics` offers no removal.
 */

import { Analytics } from '@dub/analytics/react'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { isFirstPartyTrackingSurface } from '@/lib/analytics/first-party-tracking-scope'
import { useCookieConsent } from '@/lib/privacy/use-cookie-consent'

const REFER_DOMAIN = (process.env.NEXT_PUBLIC_DUB_REFER_DOMAIN || '').trim().toLowerCase()
const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || 'maiteams.com').toLowerCase()
const LEAD_FORM_DOMAIN = (process.env.NEXT_PUBLIC_LEAD_FORM_DOMAIN || 'leads.yourdomain.com').toLowerCase()

/** Matches `REFERRAL_CONFIG.attributionWindowDays`: the sales cycle is not self-serve. */
export const DUB_COOKIE_EXPIRES_IN_DAYS = 90

export default function DubAnalytics() {
  const pathname = usePathname()
  // Referral attribution reports a conversion to a partner, so it waits for the
  // cookie choice and a Global Privacy Control signal keeps it off
  // (lib/preferences/cookie-consent.ts).
  const consent = useCookieConsent()
  // The browser location is only knowable after mount; rendering nothing on
  // the server pass is correct (the script is client-only anyway) and avoids a
  // hydration mismatch. Remember the pathname captured with it so a client
  // navigation cannot evaluate the new path against the previous query.
  const [browserLocation, setBrowserLocation] = useState<{
    hostname: string
    pathname: string
    search: string
  } | null>(null)
  useEffect(() => {
    setBrowserLocation({
      hostname: window.location.hostname,
      pathname,
      search: window.location.search,
    })
  }, [pathname])

  if (!REFER_DOMAIN || !consent.permissions.saleOrSharing) return null
  if (!browserLocation || browserLocation.pathname !== pathname || !pathname) return null
  if (
    !isFirstPartyTrackingSurface({
      hostname: browserLocation.hostname,
      pathname,
      search: browserLocation.search,
      appRootDomain: APP_ROOT_DOMAIN,
      leadFormDomain: LEAD_FORM_DOMAIN,
    })
  ) {
    return null
  }

  return (
    <Analytics
      domainsConfig={{ refer: REFER_DOMAIN }}
      cookieOptions={{ expiresInDays: DUB_COOKIE_EXPIRES_IN_DAYS }}
    />
  )
}
